// Bundle-side bridge to the pre-app bootstrap that each You `index.html` runs
// inline, before any bundle is fetched. The bootstrap owns the splash, the
// mount watchdog and the raw Mixpanel/Sentry beacon; everything here is a
// best-effort call into it, because the bundle must keep working when the
// bootstrap is absent (tests, the federated remote loaded by another shell, an
// older cached index.html).
export const PRE_APP_GLOBAL = '__YOU_PRE_APP__';

// Copy for the pre-app error screen. The loading state is the spinner alone —
// no text — so these are the only strings the layer needs. The inline bootstrap
// in each `index.html` carries a literal copy of them because it runs before
// any bundle exists and cannot import them; the two must change together (see
// apps/you/CLAUDE.md).
export const PRE_APP_COPY = Object.freeze({
  pt: Object.freeze({
    title: 'Não foi possível carregar',
    message: 'Verifique sua conexão e tente novamente.',
    retry: 'Tentar novamente',
  }),
  en: Object.freeze({
    title: 'We could not load this page',
    message: 'Check your connection and try again.',
    retry: 'Try again',
  }),
  es: Object.freeze({
    title: 'No fue posible cargar',
    message: 'Revisa tu conexión e inténtalo de nuevo.',
    retry: 'Intentar de nuevo',
  }),
});

export const readDocumentLang = () => {
  try {
    return (
      globalThis?.document?.documentElement?.lang ||
      globalThis?.navigator?.language ||
      'pt'
    );
  } catch {
    return 'pt';
  }
};

export const resolvePreAppCopy = (lang = readDocumentLang()) => {
  const key = String(lang ?? '')
    .slice(0, 2)
    .toLowerCase();
  return PRE_APP_COPY[key] ?? PRE_APP_COPY.pt;
};

export const PRE_APP_EVENT = Object.freeze({
  BOOTSTRAP_RETRY: 'pre_app_bootstrap_retry',
  BOOTSTRAP_RECOVERED: 'pre_app_bootstrap_recovered',
  BOOTSTRAP_FAILED: 'pre_app_bootstrap_failed',
  REMOTE_RETRY: 'pre_app_remote_retry',
  REMOTE_RECOVERED: 'pre_app_remote_recovered',
  REMOTE_FAILED: 'pre_app_remote_failed',
});

const _readPreApp = () => {
  try {
    return globalThis?.[PRE_APP_GLOBAL] ?? null;
  } catch {
    return null;
  }
};

export const isPreAppAvailable = () =>
  typeof _readPreApp()?.track === 'function';

export const trackPreApp = (name, properties = {}) => {
  const preApp = _readPreApp();
  if (typeof preApp?.track !== 'function') return false;

  try {
    preApp.track(name, properties);
    return true;
  } catch {
    return false;
  }
};

// Disarms the watchdog and removes the splash. Called once the React root has
// rendered — this is also the only signal that turns the `pre_app_arrived`
// beacon into a measurable time-to-mount instead of a dangling arrival.
export const markAppMounted = (properties = {}) => {
  const preApp = _readPreApp();
  if (typeof preApp?.markMounted !== 'function') return false;

  try {
    preApp.markMounted(properties);
    return true;
  } catch {
    return false;
  }
};

// Swaps the splash for the friendly error screen with a manual retry. Plain DOM
// inside index.html, so it still paints when React itself never loaded.
export const showAppLoadError = (properties = {}) => {
  const preApp = _readPreApp();
  if (typeof preApp?.showError !== 'function') return false;

  try {
    preApp.showError(properties);
    return true;
  } catch {
    return false;
  }
};

// The bundle finished downloading, parsing and executing. Call it from the very
// top of the app's bootstrap module, BEFORE anything it awaits — the whole point
// is to separate "the bundle arrived" from "the app was allowed to render", which
// in the payment app is gated on a LaunchDarkly round trip.
export const markBundleReady = (properties = {}) => {
  const preApp = _readPreApp();
  if (typeof preApp?.markBundleReady !== 'function') return false;

  try {
    preApp.markBundleReady(properties);
    return true;
  } catch {
    return false;
  }
};

// The first product screen the user can actually read. Called once per page
// load from the first page view any You app reports, which is what closes the
// "first HTML byte → screen on the user's eyes" measurement.
export const markFirstScreen = (properties = {}) => {
  const preApp = _readPreApp();
  if (typeof preApp?.markFirstScreen !== 'function') return false;

  try {
    preApp.markFirstScreen(properties);
    return true;
  } catch {
    return false;
  }
};

// Embedded in the merchant's checkout the screen is painted but still hidden
// until the host reveals the iframe, so this is when it truly becomes visible.
export const markSdkRevealed = (properties = {}) => {
  const preApp = _readPreApp();
  if (typeof preApp?.markSdkRevealed !== 'function') return false;

  try {
    preApp.markSdkRevealed(properties);
    return true;
  } catch {
    return false;
  }
};

// Milliseconds since the browser started navigating to the document. Falls back
// to null when the bootstrap is absent, so callers can omit the field instead
// of reporting a duration measured from the wrong origin.
export const msSinceNavigationStart = () => {
  const preApp = _readPreApp();
  if (typeof preApp?.sinceNavigationStart !== 'function') return null;

  try {
    return preApp.sinceNavigationStart();
  } catch {
    return null;
  }
};

export const readPreAppStartedAt = () => {
  const startedAt = _readPreApp()?.startedAt;
  return typeof startedAt === 'number' ? startedAt : null;
};

export const msSincePreAppStart = () => {
  const startedAt = readPreAppStartedAt();
  return startedAt === null ? null : Date.now() - startedAt;
};

// Errors do not survive `postMessage`/JSON, and Mixpanel rejects deep payloads,
// so failures travel as a flat, already-stringified shape.
export const serializePreAppError = (error) => {
  if (!error) return { errorName: null, errorMessage: null };

  if (typeof error === 'string')
    return { errorName: 'Error', errorMessage: error };

  return {
    errorName: error.name ?? 'Error',
    errorMessage: String(error.message ?? error).slice(0, 500),
  };
};
